import { useState } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../components/States";
import { hasModule } from "../lib/config";
import { crmOutboxProblems, retryOutboxItem } from "../lib/data";
import { fmtDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";

const ADAPTER_LABEL: Record<string, string> = {
  noop: "Not connected (events are recorded but not forwarded)",
  supabase: "Built-in CRM tables (crm schema)",
  http: "Connected CRM instance",
};

/**
 * The CRM itself is provided by the platform's CRM package and plugs in through
 * the outbox; this screen only shows delivery health.
 */
/** What this client sends to the CRM, from its portal modules: "Leads and customers", or with quotes and bookings. */
function synced(): string {
  const items = ["Leads", ...(hasModule("quotes") ? ["quotes"] : []), ...(hasModule("bookings") ? ["bookings"] : []), "customers"];
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function Crm() {
  const [busy, setBusy] = useState<string | null>(null);
  const { notice, setNotice, fail } = useNotice();
  const { data, error, loading, reload } = useAsync(crmOutboxProblems);

  async function retry(id: string) {
    setBusy(id);
    setNotice(null);
    try {
      await retryOutboxItem(id);
      reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  const forbidden = error?.startsWith("403");

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Integrations"
        title="CRM sync"
        meta={`${synced()} are sent to the platform CRM automatically through a delivery queue, so nothing needs to be entered twice. This page shows whether that queue is healthy.`}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {loading && !data && <Skeleton rows={3} columns={4} />}
      {forbidden && <EmptyState icon="warning" title="Directors only">Queue health is visible to directors only.</EmptyState>}
      {error && !forbidden && <ErrorState message={error} onRetry={reload} />}

      {data && (
        <>
          <div className="kpis">
            <div className="kpi"><span className="kpi__label">Waiting</span><span className="kpi__value">{data.counts.pending}</span>
              {data.oldestPendingAt && <span className="kpi__sub">Oldest {fmtDate(data.oldestPendingAt)}</span>}</div>
            <div className={`kpi${data.counts.failed ? " kpi--warn" : ""}`}><span className="kpi__label">Retrying</span><span className="kpi__value">{data.counts.failed}</span></div>
            <div className={`kpi kpi--alert${data.counts.dead ? " kpi--hot" : ""}`}><span className="kpi__label">Needs attention</span><span className="kpi__value">{data.counts.dead}</span></div>
            <div className="kpi"><span className="kpi__label">Delivered</span><span className="kpi__value">{data.counts.sent}</span></div>
          </div>
          <p className="muted small">Destination: {ADAPTER_LABEL[data.adapter] ?? data.adapter}</p>

          <div className="card card--table">
            <div className="card__head"><h2 className="card__title">Problems</h2></div>
            {data.items.length === 0 ? (
              <EmptyState help icon="check" title="No failed deliveries">Every event has been delivered or is waiting its turn.</EmptyState>
            ) : (
              <table className="table table--stack">
                <thead><tr><th scope="col">Created</th><th scope="col">Event</th><th scope="col">Status</th><th scope="col" className="n">Attempts</th><th scope="col">Last error</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {data.items.map((i) => (
                    <tr key={i.id}>
                      <td data-label="Created" className="nowrap">{fmtDate(i.created_at)}</td>
                      <td data-label="Event" className="mono">{i.event_type}</td>
                      <td data-label="Status"><Chip value={i.status}>{i.status === "dead" ? "Stopped" : "Retrying"}</Chip>{i.status === "failed" && <span className="sub">Next try {fmtDate(i.next_attempt_at)}</span>}</td>
                      <td data-label="Attempts" className="n">{i.attempts}</td>
                      <td data-label="Last error" className="small">{i.last_error}</td>
                      <td data-label="Actions" className="actions">
                        <button type="button" className="btn btn--outline btn--xs" disabled={busy === i.id} onClick={() => retry(i.id)}>
                          <Icon name="refresh" size={16} /> {busy === i.id ? "Retrying" : "Retry now"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}
